<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
            <app-page-surface-header titleIcon="clients"
                [compact]="true"
                title="Edit client"
                subtitle="Update client profile, status, and commercial details."
            />
            <p *ngIf="uniqueIdLabel" class="text-xs text-gray-500 dark:text-gray-400">{{ uniqueIdLabel }}</p>
        </div>
    </div>

    <div *ngIf="loadError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {{ loadError }}
        <div class="mt-4">
            <a routerLink="/manage-client" class="btn inline-flex rounded-full bg-white px-4 py-2 text-black">Back to clients</a>
        </div>
    </div>

    <form *ngIf="!loadError" (ngSubmit)="onSubmit()" class="space-y-6">
        <fieldset [disabled]="bootstrapLoading" class="rounded-3xl border-0 bg-white/60 p-6 shadow-sm dark:bg-[#0e1726] md:p-8 [&:disabled]:opacity-60">
            <!-- Basic Details -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Basic Details</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Provide your business and contact information accurately.</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-red-600">*</span> Required field
                </p>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
                <div>
                    <label for="businessName" class="mb-1.5 block text-sm font-medium text-black dark:text-white">
                        Business Name <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="businessName"
                        name="businessName"
                        type="text"
                        [(ngModel)]="businessName"
                        (blur)="maybeSuggestSubdomainFromName()"
                        class="form-input w-full rounded-lg"
                        placeholder="Legal / display name"
                    />
                    <p *ngIf="fieldErrors.businessName" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.businessName }}</p>
                </div>
                <div class="md:col-span-1">
                    <label for="tenantSubdomain" class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                        <span>Client subdomain <span class="font-semibold text-red-600" aria-hidden="true">*</span></span>
                        <span class="text-xs font-normal text-gray-500 dark:text-gray-400">3–63 chars, lowercase, hyphens</span>
                    </label>
                    <div class="flex gap-2">
                        <input
                            id="tenantSubdomain"
                            name="tenantSubdomain"
                            type="text"
                            [(ngModel)]="tenantSubdomain"
                            autocomplete="off"
                            class="form-input min-w-0 flex-1 rounded-lg lowercase"
                            placeholder="e.g. acme-finance"
                            maxlength="63"
                        />
                        <button
                            type="button"
                            class="btn shrink-0 rounded-lg bg-[#FFD85F] px-3 text-xs font-semibold text-black hover:opacity-90"
                            (click)="suggestSubdomain()"
                        >
                            Suggest
                        </button>
                    </div>
                    <p *ngIf="fieldErrors.tenantSubdomain" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.tenantSubdomain }}</p>
                </div>
                <div>
                    <label for="tenantStatus" class="mb-1.5 block text-sm font-medium text-black dark:text-white">
                        Status <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <select
                        id="tenantStatus"
                        name="tenantStatus"
                        [(ngModel)]="tenantStatus"
                        class="form-select w-full rounded-lg border-white-light py-3 dark:border-[#191e3a] dark:bg-dark"
                    >
                        <option value="Active">Active</option>
                        <option value="InActive">Inactive</option>
                        <option value="Blocked">Blocked</option>
                        <option value="Suspended">Suspended</option>
                        <option value="Locked">Locked</option>
                    </select>
                </div>
                <div class="md:col-span-1">
                    <app-intl-phone-field
                        #contactPhoneField
                        inputId="contactNumber"
                        name="contactNumber"
                        label="Contact Number"
                        placeholder="Phone number"
                        [(countryIso)]="contactCountryIso"
                        [(nationalNumber)]="contactNational"
                        [disabled]="bootstrapLoading"
                    />
                    <p *ngIf="fieldErrors.contactPhone" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.contactPhone }}</p>
                </div>
                <div>
                    <label for="contactPerson" class="mb-1.5 block text-sm font-medium">Contact Person</label>
                    <input
                        id="contactPerson"
                        name="contactPerson"
                        type="text"
                        [(ngModel)]="contactPerson"
                        class="form-input w-full rounded-lg"
                        placeholder="Enter Name"
                    />
                </div>

                <div class="md:col-span-1">
                    <app-intl-phone-field
                        #personPhoneField
                        inputId="contactPersonPhone"
                        name="contactPersonPhone"
                        label="Contact Person Phone Number"
                        placeholder="Phone number"
                        [(countryIso)]="contactPersonCountryIso"
                        [(nationalNumber)]="contactPersonNational"
                        [disabled]="bootstrapLoading"
                    />
                    <p *ngIf="fieldErrors.contactPersonPhone" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.contactPersonPhone }}</p>
                </div>
                <div class="w-full min-w-0 md:col-span-3">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5 md:gap-y-6">
                        <div class="min-w-0">
                            <label for="email" class="mb-1.5 block text-sm font-medium text-black dark:text-white">
                                Email <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                [(ngModel)]="email"
                                class="form-input w-full rounded-lg"
                                placeholder="Enter Email"
                            />
                            <p *ngIf="fieldErrors.email" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.email }}</p>
                        </div>
                        <div class="min-w-0">
                            <label for="gstn" class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>GST number (GSTIN) <span class="font-semibold text-red-600" aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Required · 15-character Indian GSTIN</span>
                            </label>
                            <input
                                id="gstn"
                                name="gstn"
                                type="text"
                                [(ngModel)]="gstn"
                                (blur)="blurNormalizeGst()"
                                maxlength="15"
                                class="form-input w-full rounded-lg uppercase"
                                placeholder="15-character GSTIN"
                                autocomplete="off"
                            />
                            <p *ngIf="fieldErrors.gstn" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.gstn }}</p>
                        </div>
                        <div class="min-w-0">
                            <label for="billingAddress"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Billing address</span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Optional · printed on invoices</span>
                            </label>
                            <input id="billingAddress" name="billingAddress" type="text" [(ngModel)]="billingAddress"
                                maxlength="255" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <label for="billingCity" class="mb-1.5 text-sm font-medium text-black dark:text-white">City</label>
                            <input id="billingCity" name="billingCity" type="text" [(ngModel)]="billingCity"
                                maxlength="100" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <label for="billingPincode" class="mb-1.5 text-sm font-medium text-black dark:text-white">Pincode</label>
                            <input id="billingPincode" name="billingPincode" type="text" [(ngModel)]="billingPincode"
                                maxlength="12" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <div class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span class="text-sm font-medium text-black dark:text-white">Company logo</span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Optional · client brand image</span>
                            </div>
                            <div
                                class="flex min-h-[38px] min-w-0 flex-wrap items-center gap-3 rounded-lg border border-white-light bg-white px-2 py-1 dark:border-[#191e3a] dark:bg-dark"
                            >
                                <label
                                    class="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#FFD85F] px-4 py-1 mb-0 text-xs font-semibold text-black transition hover:opacity-90"
                                >
                                    <icon-folder-plus class="size-4 shrink-0" />
                                    Choose logo
                                    <input type="file" accept="image/*" class="sr-only" (change)="onLogoSelected($event)" />
                                </label>
                                <span class="min-w-0 truncate text-sm text-gray-500 dark:text-gray-400">{{ logoFileName || existingLogoHint || 'No file chosen' }}</span>
                            </div>
                            <div *ngIf="logoPreviewUrl" class="mt-3 flex flex-wrap items-center gap-3">
                                <img
                                    [src]="logoPreviewUrl"
                                    alt="Current company logo"
                                    class="h-16 w-44 shrink-0 rounded-xl border border-gray-200 bg-white object-contain dark:border-[#191e3a] dark:bg-[#0e1726]"
                                />
                                <button
                                    type="button"
                                    class="btn rounded-lg border border-danger px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/10 disabled:opacity-50"
                                    (click)="unlinkLogo()"
                                    [disabled]="unlinkLogoBusy || bootstrapLoading"
                                >
                                    {{ unlinkLogoBusy ? 'Removing…' : 'Remove logo' }}
                                </button>
                            </div>
                        </div>

                        <div class="min-w-0 md:self-start">
                            <label for="businessPan" class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Business PAN <span class="font-semibold text-red-600" aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Required · format ABCDE1234F</span>
                            </label>
                            <input
                                id="businessPan"
                                name="businessPan"
                                type="text"
                                [(ngModel)]="businessPan"
                                (blur)="blurNormalizePan()"
                                maxlength="10"
                                class="form-input w-full rounded-lg uppercase"
                                placeholder="ABCDE1234F"
                                autocomplete="off"
                            />
                            <p *ngIf="fieldErrors.businessPan" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.businessPan }}</p>
                        </div>
                        <div class="min-w-0 md:col-span-2 md:self-start">
                            <div class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span class="text-sm font-medium text-black dark:text-white">
                                    PAN card image <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                                </span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Scan or photo · same PAN as left</span>
                            </div>
                            <div
                                class="flex min-h-[38px] min-w-0 flex-wrap items-center gap-3 rounded-lg border border-white-light bg-white px-2 py-1 dark:border-[#191e3a] dark:bg-dark"
                            >
                                <label
                                    class="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-[#FFD85F] px-4 py-1 mb-0 text-xs font-semibold text-black transition hover:opacity-90"
                                >
                                    <icon-folder-plus class="size-4 shrink-0" />
                                    Choose PAN image
                                    <input type="file" accept="image/*" class="sr-only" (change)="onProfilePictureSelected($event)" />
                                </label>
                                <span class="min-w-0 flex-1 truncate text-sm text-gray-500 dark:text-gray-400">{{ profilePictureFileName || existingProfilePictureHint || 'No file chosen' }}</span>
                            </div>
                            <div *ngIf="panPreviewUrl" class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                                <img
                                    [src]="panPreviewUrl"
                                    alt="PAN card preview"
                                    class="max-h-48 max-w-full rounded-lg border border-gray-200 object-contain dark:border-[#191e3a]"
                                />
                                <button
                                    type="button"
                                    class="btn shrink-0 self-start rounded-lg border border-danger px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/10 disabled:opacity-50"
                                    (click)="unlinkPanImage()"
                                    [disabled]="unlinkPanBusy || bootstrapLoading"
                                >
                                    {{ unlinkPanBusy ? 'Removing…' : 'Remove PAN image' }}
                                </button>
                            </div>
                            <p *ngIf="fieldErrors.panImage" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.panImage }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- Product types -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Product types</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select product types from the catalog (loaded from the API).</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-red-600">*</span> At least one product type required
                </p>
            </div>

            <div class="mt-6 space-y-6">
                <p *ngIf="bootstrapLoading" class="text-sm text-gray-500 dark:text-gray-400">
                    Loading product types…
                </p>
                <p *ngIf="!bootstrapLoading && catalogLoadError" class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                    {{ catalogLoadError }}
                </p>

                <div *ngIf="!bootstrapLoading && !catalogLoadError">
                    <div>
                        <div class="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span class="text-sm font-medium text-black dark:text-white">
                                Product type <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                            </span>
                            <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Pick at least one type.</span>
                        </div>
                        <p *ngIf="fieldErrors.products" class="mb-2 text-xs text-red-600 dark:text-red-400" role="alert">{{ fieldErrors.products }}</p>
                        <div class="flex flex-wrap gap-4">
                            <label
                                *ngFor="let row of productsCatalog; trackBy: trackBySlug"
                                class="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white-light bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-dark"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox"
                                    [name]="'product-' + row.slug"
                                    [(ngModel)]="productSelection[row.slug]"
                                />
                                {{ row.name }}
                            </label>
                        </div>

                        <!-- Billing: type drives whether per-PD rates and frequency apply. -->
                        <div class="mb-6 mt-4">
                            <h3 class="mb-3 text-base font-semibold">Billing</h3>

                            <fieldset class="mb-4 min-w-0 border-0 p-0">
                                <legend class="mb-1.5 block text-sm font-medium">Billing Type <span
                                        class="text-danger">*</span></legend>
                                <div
                                    class="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-700">
                                    <button type="button" (click)="onBillingTypeChange('PREPAID')"
                                        [class]="!isPostpaid ? 'bg-primary px-6 py-2 text-sm text-white' : 'px-6 py-2 text-sm'"
                                        [attr.aria-pressed]="!isPostpaid">
                                        Pre-Paid
                                    </button>
                                    <button type="button" (click)="onBillingTypeChange('POSTPAID')"
                                        [class]="isPostpaid ? 'bg-primary px-6 py-2 text-sm text-white' : 'px-6 py-2 text-sm'"
                                        [attr.aria-pressed]="isPostpaid">
                                        Post-Paid
                                    </button>
                                </div>
                            </fieldset>

                            <div *ngIf="isPostpaid">
                                <label for="billing-frequency" class="mb-1.5 block text-sm font-medium">
                                    Frequency <span class="text-danger">*</span>
                                </label>
                                <select id="billing-frequency" name="billing-frequency" class="form-select"
                                    [(ngModel)]="billingFrequency" (ngModelChange)="clearSubmitErrorOnly()">
                                    <option value="" disabled>Select frequency</option>
                                    <option *ngFor="let f of billingFrequencies" [value]="f.value">{{ f.label }}
                                    </option>
                                </select>
                            </div>

                            <p *ngIf="!isPostpaid" class="text-sm text-gray-500 dark:text-gray-400">
                                Pre-paid tenants are billed through subscription plans, not per PD.
                            </p>
                        </div>

                        <!-- Per-PD charge for each selected product type -->
                        <ng-container *ngFor="let row of productsCatalog; trackBy: trackBySlug">
                            <div *ngIf="productSelection[row.slug] && isPostpaid" class="mt-3">
                                <label [for]="'charge-' + row.slug" class="mb-1.5 block text-sm font-medium">
                                    {{ row.name }} — charge per PD ({{ currencySymbol }})
                                </label>
                                <input [id]="'charge-' + row.slug" [name]="'charge-' + row.slug" type="text"
                                    inputmode="decimal" class="form-input w-full rounded-lg"
                                    [(ngModel)]="productCharges[row.slug]" placeholder="e.g. 50.00" />
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- One Time Fee Structure -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">One Time Fee Structure</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Enter and upload details related to your one-time payment.</p>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
                <div>
                    <label for="amountReceived" class="mb-1.5 block text-sm font-medium">Amount Received</label>
                    <input
                        id="amountReceived"
                        name="amountReceived"
                        type="text"
                        inputmode="decimal"
                        [(ngModel)]="amountReceived"
                        class="form-input w-full rounded-lg"
                        placeholder="00000"
                    />
                </div>
                <div>
                    <label for="dateOfReceipt" class="mb-1.5 block text-sm font-medium">Date of Receipt</label>
                    <div class="relative">
                        <input
                            id="dateOfReceipt"
                            type="text"
                            name="dateOfReceipt"
                            mwlFlatpickr
                            [(ngModel)]="dateOfReceipt"
                            (ngModelChange)="onDateOfReceiptChange()"
                            [dateFormat]="'d/m/Y'"
                            [convertModelValue]="true"
                            [disableMobile]="true"
                            [minDate]="'today'"
                            placeholder="Select Date of Receipt"
                            class="form-input w-full rounded-lg ltr:pr-10 rtl:pl-10"
                        />
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pe-3">
                            <icon-calendar class="size-5 text-gray-500 dark:text-gray-400" />
                        </div>
                    </div>
                    <p *ngIf="fieldErrors.dateOfReceipt" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                        {{ fieldErrors.dateOfReceipt }}
                    </p>
                </div>
                <div>
                    <label for="unNumber" class="mb-1.5 block text-sm font-medium">UN Number</label>
                    <input
                        id="unNumber"
                        name="unNumber"
                        type="text"
                        [(ngModel)]="unNumber"
                        class="form-input w-full rounded-lg"
                        placeholder="Enter UN Number"
                    />
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- Post Paid -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Post Paid</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Per-PD charges are set per product type above (under Product types).</p>
            </div>

            <div class="mt-8 space-y-4 border-t border-dashed border-gray-300 pt-6 dark:border-[#191e3a]">
                <div
                    *ngIf="submitError"
                    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
                    role="alert"
                >
                    {{ submitError }}
                </div>
                <div class="flex flex-wrap justify-end gap-3">
                    <a routerLink="/manage-client" class="btn bg-white text-black rounded-full px-6">Cancel</a>
                    <button
                        type="submit"
                        class="btn rounded-full bg-[#303030] px-8 font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                        [disabled]="submitting || bootstrapLoading"
                    >
                        {{ submitting ? 'Saving…' : 'Update' }}
                    </button>
                </div>
            </div>
        </fieldset>
    </form>

    <!-- Success modal: centered overlay, auto-redirect after 2s -->
    <dialog
        *ngIf="showSuccessModal"
        open
        class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
        aria-labelledby="edit-client-success-title"
        (keydown.escape)="closeSuccessModal()"
    >
        <button type="button" class="absolute inset-0 bg-black/50 backdrop-blur-[1px]" (click)="closeSuccessModal()" aria-label="Close dialog"></button>
        <div
            class="relative z-10 w-full max-w-md rounded-3xl bg-white px-8 pb-10 pt-14 text-center shadow-2xl dark:bg-[#0e1726]"
        >
            <button
                type="button"
                class="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-[#18214d] transition hover:bg-gray-100 dark:text-white-dark dark:hover:bg-white/10"
                (click)="closeSuccessModal()"
                aria-label="Close"
            >
                <icon-x-circle class="size-5 hover:text-danger" />
            </button>

            <div class="mx-auto mb-6 flex h-[7.5rem] w-[7.5rem] items-center justify-center">
                <svg
                    class="text-emerald-500"
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <g transform="translate(60,60)">
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(0)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(45)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(90)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(135)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(180)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(225)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(270)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" transform="rotate(315)" />
                    </g>
                    <circle cx="60" cy="60" r="28" fill="#22c55e" />
                    <path
                        d="M48 61 L56 69 L74 49"
                        stroke="white"
                        stroke-width="3.2"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>

            <h2 id="edit-client-success-title" class="text-lg font-bold text-black dark:text-white md:text-xl">
                Client updated
            </h2>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Changes were saved successfully.</p>
        </div>
</dialog>
